/**
 * ImageViewModal Component
 *
 * Fullscreen modal for viewing generated images with action buttons.
 */

import { X, Download, Wand2, Trash2 } from 'lucide-react';
import { Dialog, DialogOverlay, DialogPortal } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import type { IImageViewModalProps } from '@/@types/avatars';

export const ImageViewModal = ({
  image,
  isOpen,
  onClose,
  onDownload,
  onEdit,
  onDelete,
}: IImageViewModalProps) => {
  if (!image) return null;

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && onClose()}>
      <DialogPortal>
        <DialogOverlay className="bg-black/90" />
        <DialogPrimitive.Content
          className="fixed inset-0 z-50 flex items-center justify-center p-4 outline-none"
          onEscapeKeyDown={onClose}
        >
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center text-white transition-colors z-10"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Image Container */}
          <div className="relative max-w-[90vw] max-h-[85vh]">
            <img
              src={image.url}
              alt="Generated avatar fullscreen"
              className="max-w-full max-h-[85vh] object-contain rounded-lg"
            />

            {/* Action Buttons (bottom) */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 p-2 rounded-full bg-black/70 backdrop-blur-sm">
              <Button
                variant="ghost"
                size="sm"
                onClick={onDownload}
                className="text-white hover:bg-white/20 hover:text-white"
              >
                <Download className="w-4 h-4" />
                <span>Download</span>
              </Button>

              <div className="w-px h-6 bg-white/30" />

              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  onEdit();
                  onClose();
                }}
                className="text-white hover:bg-white/20 hover:text-white"
              >
                <Wand2 className="w-4 h-4" />
                <span>Edit</span>
              </Button>

              <div className="w-px h-6 bg-white/30" />

              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  onDelete();
                  onClose();
                }}
                className="text-destructive hover:bg-destructive/20 hover:text-destructive"
              >
                <Trash2 className="w-4 h-4" />
                <span>Delete</span>
              </Button>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
};
